// src/products/dataroom/components/Modals/DocumentViewer.js
import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Shield, AlertCircle, FileText, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../../../config';
import { formatDateTime } from '../../utils/dateUtils';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
 
const DocumentViewer = ({ 
  isOpen, 
  onClose, 
  item, 
  projectId, 
  user 
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [watermarkedUrl, setWatermarkedUrl] = useState(null);
  const [wordDocHtml, setWordDocHtml] = useState(null); // HTML content for Word documents
  const [excelHtml, setExcelHtml] = useState(null); // HTML content for Excel files
  const [powerpointPdfUrl, setPowerpointPdfUrl] = useState(null); // PDF URL for PowerPoint files (converted from PPTX)
  const [textContent, setTextContent] = useState(null); // Text content for .txt files
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const blobUrlsRef = React.useRef([]); // Track blob URLs for cleanup
 
  // Watermark image with user info and confidential marking
  const watermarkImage = useCallback(async (imageUrl, userEmail) => {
    try {
      console.log('Starting image watermarking...', { imageUrl, userEmail });
      
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        // Handle CORS - try anonymous first, fallback if needed
        img.crossOrigin = 'anonymous';
        
        // Set timeout for loading
        const timeout = setTimeout(() => {
          reject(new Error('Image loading timeout'));
        }, 30000); // 30 second timeout
        
        img.onload = () => {
          clearTimeout(timeout);
          try {
            console.log('Image loaded, creating watermark...', { width: img.width, height: img.height });
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw original image
            ctx.drawImage(img, 0, 0);
            
            // Watermark settings - make them more visible
            const timestamp = formatDateTime(new Date().toISOString());
            const baseFontSize = Math.max(32, Math.min(img.width, img.height) / 15);
            
            const watermarkText = `CONFIDENTIAL - ${userEmail} - ${timestamp}`;
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            
            // 1. Large diagonal "CONFIDENTIAL" across center (like PDF)
            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate(-Math.PI / 4); // -45 degrees
            ctx.globalAlpha = 0.3;
            ctx.font = `bold ${baseFontSize * 2}px Arial`;
            ctx.fillStyle = 'rgba(230, 20, 20, 0.8)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('CONFIDENTIAL', 0, 0);
            ctx.restore();
            
            // 2. Top center "CONFIDENTIAL"
            ctx.save();
            ctx.globalAlpha = 0.4;
            ctx.font = `bold ${baseFontSize * 0.8}px Arial`;
            ctx.fillStyle = 'rgba(230, 20, 20, 1)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText('CONFIDENTIAL', centerX, 30);
            ctx.restore();
            
            // 3. Bottom center with user info (footer)
            ctx.save();
            ctx.globalAlpha = 0.9;
            ctx.font = `bold ${baseFontSize * 0.6}px Arial`;
            ctx.fillStyle = 'rgba(150, 0, 0, 1)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            const bottomText = `Viewed by ${userEmail} on ${timestamp} - CONFIDENTIAL`;
            ctx.fillText(bottomText, centerX, canvas.height - 20);
            ctx.restore();
            
            // 4. User email and timestamp at 1/3 height (like PDF)
            ctx.save();
            ctx.translate(centerX, canvas.height * 0.33);
            ctx.rotate(-Math.PI / 4);
            ctx.globalAlpha = 0.3;
            ctx.font = `bold ${baseFontSize}px Arial`;
            ctx.fillStyle = 'rgba(230, 20, 20, 0.8)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(watermarkText, 0, 0);
            ctx.restore();
            
            // Convert to blob URL
            canvas.toBlob((blob) => {
              if (!blob) {
                console.error('Failed to create blob from canvas');
                reject(new Error('Failed to create watermarked image blob'));
                return;
              }
              const blobUrl = URL.createObjectURL(blob);
              blobUrlsRef.current.push(blobUrl); // Track for cleanup
              console.log('Image watermarking completed successfully');
              resolve(blobUrl);
            }, 'image/png', 0.95);
          } catch (canvasError) {
            console.error('Canvas error during watermarking:', canvasError);
            reject(new Error(`Canvas error: ${canvasError.message}`));
          }
        };
        
        img.onerror = (error) => {
          clearTimeout(timeout);
          console.error('Error loading image for watermarking:', error);
          console.error('Image URL:', imageUrl);
          // If CORS fails, try without crossOrigin
          if (img.crossOrigin === 'anonymous') {
            console.log('Retrying without CORS...');
            img.crossOrigin = null;
            img.src = imageUrl;
            return;
          }
          reject(new Error('Failed to load image for watermarking. CORS may be blocking access.'));
        };
        
        img.src = imageUrl;
      });
    } catch (error) {
      console.error('Error watermarking image:', error);
      throw error;
    }
  }, []);

  // Convert Word document to HTML with watermarking
  const convertWordToHtml = useCallback(async (wordUrl, userEmail, fileName) => {
    try {
      console.log('Starting Word document conversion...', { wordUrl, userEmail, fileName });
      
      // Check if it's .doc (old format) - mammoth only supports .docx
      if (fileName && fileName.toLowerCase().endsWith('.doc') && !fileName.toLowerCase().endsWith('.docx')) {
        throw new Error('Preview is only available for .docx files. Please convert your .doc file to .docx format or download to view.');
      }
      
      // Fetch the Word document
      const response = await fetch(wordUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Convert DOCX to HTML using mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer }, {
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
        ]
      });
      
      const html = result.value;
      const timestamp = formatDateTime(new Date().toISOString());
      
      // Create watermarked HTML with user info
      const watermarkedHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              background: white;
              position: relative;
            }
            .watermark {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
              z-index: 1000;
            }
            .watermark-diagonal {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 72px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
            .watermark-top {
              position: absolute;
              top: 30px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 24px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.4);
            }
            .watermark-bottom {
              position: absolute;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 12px;
              font-weight: bold;
              color: rgba(150, 0, 0, 0.9);
              text-align: center;
              width: 100%;
            }
            .watermark-third {
              position: absolute;
              top: 33%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 36px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
          </style>
        </head>
        <body>
          <div class="watermark">
            <div class="watermark-diagonal">CONFIDENTIAL</div>
            <div class="watermark-top">CONFIDENTIAL</div>
            <div class="watermark-bottom">Viewed by ${userEmail} on ${timestamp} - CONFIDENTIAL</div>
            <div class="watermark-third">CONFIDENTIAL - ${userEmail} - ${timestamp}</div>
          </div>
          ${html}
        </body>
        </html>
      `;
      
      console.log('Word document conversion completed successfully');
      return watermarkedHtml;
    } catch (error) {
      console.error('Error converting Word document:', error);
      throw error;
    }
  }, []);

  // Convert Excel file to HTML with watermarking
  const convertExcelToHtml = useCallback(async (excelUrl, userEmail, fileName) => {
    try {
      console.log('Starting Excel conversion...', { excelUrl, userEmail, fileName });
      
      // Fetch the Excel file
      const response = await fetch(excelUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Read the workbook
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get all sheet names
      const sheetNames = workbook.SheetNames;
      
      if (sheetNames.length === 0) {
        throw new Error('Excel file has no sheets');
      }
      
      const timestamp = formatDateTime(new Date().toISOString());
      
      // Convert all sheets to HTML
      let sheetsHtml = '';
      sheetNames.forEach((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const html = XLSX.utils.sheet_to_html(worksheet, { 
          id: `sheet-${index}`,
          editable: false 
        });
        
        sheetsHtml += `
          <div class="excel-sheet" data-sheet-name="${sheetName}" style="display: ${index === 0 ? 'block' : 'none'}">
            <h3 class="sheet-name">${sheetName}</h3>
            ${html}
          </div>
        `;
      });
      
      // Create watermarked HTML
      const watermarkedHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              position: relative;
            }
            .watermark {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
              z-index: 1000;
            }
            .watermark-diagonal {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 72px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
            .watermark-top {
              position: absolute;
              top: 30px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 24px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.4);
            }
            .watermark-bottom {
              position: absolute;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 12px;
              font-weight: bold;
              color: rgba(150, 0, 0, 0.9);
              text-align: center;
              width: 100%;
            }
            .watermark-third {
              position: absolute;
              top: 33%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 36px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
            .excel-sheet {
              margin-bottom: 40px;
              page-break-after: always;
            }
            .sheet-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
              padding: 10px;
              background: #f3f4f6;
              border-bottom: 2px solid #d1d5db;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 20px;
            }
            table td, table th {
              border: 1px solid #d1d5db;
              padding: 8px;
              text-align: left;
            }
            table th {
              background-color: #f9fafb;
              font-weight: bold;
            }
            .sheet-tabs {
              display: flex;
              gap: 10px;
              margin-bottom: 20px;
              border-bottom: 2px solid #d1d5db;
            }
            .sheet-tab {
              padding: 10px 20px;
              cursor: pointer;
              background: #f3f4f6;
              border: none;
              border-bottom: 2px solid transparent;
              margin-bottom: -2px;
            }
            .sheet-tab.active {
              background: white;
              border-bottom-color: #3b82f6;
              font-weight: bold;
            }
            .sheet-content {
              overflow-x: auto;
            }
          </style>
          <script>
            // Simple tab switching functionality
            document.addEventListener('DOMContentLoaded', function() {
              const sheets = document.querySelectorAll('.excel-sheet');
              const tabs = document.querySelectorAll('.sheet-tab');
              
              tabs.forEach((tab, index) => {
                tab.addEventListener('click', () => {
                  tabs.forEach(t => t.classList.remove('active'));
                  sheets.forEach(s => s.style.display = 'none');
                  tab.classList.add('active');
                  sheets[index].style.display = 'block';
                });
              });
              
              // Show first sheet by default
              if (sheets.length > 0) {
                sheets[0].style.display = 'block';
                if (tabs.length > 0) tabs[0].classList.add('active');
              }
            });
          </script>
        </head>
        <body>
          <div class="watermark">
            <div class="watermark-diagonal">CONFIDENTIAL</div>
            <div class="watermark-top">CONFIDENTIAL</div>
            <div class="watermark-bottom">Viewed by ${userEmail} on ${timestamp} - CONFIDENTIAL</div>
            <div class="watermark-third">CONFIDENTIAL - ${userEmail} - ${timestamp}</div>
          </div>
          ${sheetNames.length > 1 ? `
            <div class="sheet-tabs">
              ${sheetNames.map((name, idx) => `<button class="sheet-tab" data-sheet="${idx}">${name}</button>`).join('')}
            </div>
          ` : ''}
          <div class="sheet-content">
            ${sheetsHtml}
          </div>
        </body>
        </html>
      `;
      
      console.log('Excel conversion completed successfully');
      return watermarkedHtml;
    } catch (error) {
      console.error('Error converting Excel file:', error);
      throw error;
    }
  }, []);

  // Convert text file to HTML with watermarking
  const convertTextToHtml = useCallback(async (textUrl, userEmail, fileName) => {
    try {
      console.log('Starting text file conversion...', { textUrl, userEmail, fileName });
      
      // Fetch the text file
      const response = await fetch(textUrl);
      const text = await response.text();
      
      const timestamp = formatDateTime(new Date().toISOString());
      
      // Escape HTML in text content
      const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
      
      // Create watermarked HTML
      const watermarkedHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Courier New', monospace;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
              background: white;
              position: relative;
              line-height: 1.6;
              color: #333;
            }
            .content {
              white-space: pre-wrap;
              word-wrap: break-word;
              background: white;
              padding: 20px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              position: relative;
              z-index: 1;
            }
            .watermark {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
              z-index: 1000;
            }
            .watermark-diagonal {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 72px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
            .watermark-top {
              position: absolute;
              top: 30px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 24px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.4);
            }
            .watermark-bottom {
              position: absolute;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 12px;
              font-weight: bold;
              color: rgba(150, 0, 0, 0.9);
              text-align: center;
              width: 100%;
            }
            .watermark-third {
              position: absolute;
              top: 33%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 36px;
              font-weight: bold;
              color: rgba(230, 20, 20, 0.3);
              white-space: nowrap;
            }
          </style>
        </head>
        <body>
          <div class="watermark">
            <div class="watermark-diagonal">CONFIDENTIAL</div>
            <div class="watermark-top">CONFIDENTIAL</div>
            <div class="watermark-bottom">Viewed by ${userEmail} on ${timestamp} - CONFIDENTIAL</div>
            <div class="watermark-third">CONFIDENTIAL - ${userEmail} - ${timestamp}</div>
          </div>
          <div class="content">${escapedText}</div>
        </body>
        </html>
      `;
      
      console.log('Text file conversion completed successfully');
      return watermarkedHtml;
    } catch (error) {
      console.error('Error converting text file:', error);
      throw error;
    }
  }, []);


  // Watermark PDF with user info and confidential marking
  const watermarkPDF = useCallback(async (pdfUrl, userEmail, userName) => {
    try {
      console.log('Starting PDF watermarking...');
      
      // Fetch the PDF
      const response = await fetch(pdfUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // Load PDF
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPages();
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // Watermark text with timestamp
      const timestamp = formatDateTime(new Date().toISOString());
      const watermarkText = `CONFIDENTIAL - ${userEmail} - ${timestamp}`;
      
      // Add watermark to each page
      pages.forEach((page) => {
        const { width, height } = page.getSize();
        
        // Watermark text configurations
        const largeFontSize = 40;
        const mediumFontSize = 18;
        const smallFontSize = 11;
        
        // Calculate text widths
        const confidentialText = 'CONFIDENTIAL';
        const confidentialWidth = font.widthOfTextAtSize(confidentialText, largeFontSize);
        const watermarkTextWidth = font.widthOfTextAtSize(watermarkText, mediumFontSize);
        
        // 1. Large "CONFIDENTIAL" watermark at 2/3 of page (upper middle)
        page.drawText(confidentialText, {
          x: width / 2 - confidentialWidth / 2,
          y: height * 0.67, // 2/3 down the page
          size: largeFontSize,
          font: font,
          color: rgb(0.9, 0.1, 0.1), // Red color
          opacity: 0.3, // Visible but not intrusive
        });
        
        // 2. "CONFIDENTIAL" text at top center
        page.drawText(confidentialText, {
          x: width / 2 - font.widthOfTextAtSize(confidentialText, 24) / 2,
          y: height - 40,
          size: 24,
          font: font,
          color: rgb(0.9, 0.1, 0.1),
          opacity: 0.4,
        });
        
        // 3. User info at bottom of each page (clearly visible)
        const bottomText = `Viewed by ${userEmail} on ${timestamp} - CONFIDENTIAL`;
        const bottomTextWidth = font.widthOfTextAtSize(bottomText, smallFontSize);
        page.drawText(bottomText, {
          x: width / 2 - bottomTextWidth / 2,
          y: 30,
          size: smallFontSize,
          font: font,
          color: rgb(0.7, 0, 0), // Dark red
          opacity: 0.9,
        });
        
        // 4. User email and timestamp watermark at 1/3 of page (lower middle)
        page.drawText(watermarkText, {
          x: width / 2 - watermarkTextWidth / 2,
          y: height * 0.33, // 1/3 down the page
          size: mediumFontSize,
          font: font,
          color: rgb(0.9, 0.1, 0.1),
          opacity: 0.3,
        });
      });
      
      // Serialize PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.push(blobUrl); // Track for cleanup
      
      console.log('PDF watermarking completed');
      return blobUrl;
    } catch (error) {
      console.error('Error watermarking PDF:', error);
      throw error;
    }
  }, []);

  const logFileAccess = useCallback(async () => {
    try {
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: user.id,
          project_id: projectId,
          action: 'file_viewed',
          details: {
            file_name: item.name,
            file_id: item.id,
            file_type: item.file_type
          }
        });
    } catch (error) {
      console.error('Error logging file access:', error);
    }
  }, [user.id, projectId, item.name, item.id, item.file_type]);
 
  const loadFile = useCallback(async () => {
    if (!item?.id || !projectId || !user?.email) return;

    try {
      setLoading(true);
      setError(null);

      if (!item.file_path) {
        setError('File path not found. The file may not have been uploaded properly.');
        setLoading(false);
        return;
      }

      console.log('Loading file with path:', item.file_path);
      console.log('File path type:', typeof item.file_path);
      console.log('File path length:', item.file_path?.length);

      // Check if it's a PowerPoint file
      const isPowerPointFile = item.file_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                               item.file_type === 'application/vnd.ms-powerpoint' ||
                               (item.name && (item.name.endsWith('.pptx') || item.name.endsWith('.ppt')));
      
      if (isPowerPointFile) {
        // Check if PDF version exists
        // NOTE: PDF is stored in the same database record (pdf_file_path column),
        // so it automatically inherits ALL the same permissions as the original PPT file.
        // Both files use the same item.id for permission checks.
        if (item.pdf_file_path) {
          console.log('PowerPoint file detected - PDF version exists, loading PDF');
          // Load the PDF version for viewing (with watermarking)
          // PDF uses same permissions as PPT since they share the same database record
          const cleanPdfPath = item.pdf_file_path.trim();
          
          try {
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
              .from('dataroom-files')
              .createSignedUrl(cleanPdfPath, 3600);
            
            if (signedUrlError || !signedUrlData?.signedUrl) {
              console.warn('Failed to create signed URL for PDF version, will try conversion');
              // Fall through to conversion attempt
            } else {
              const pdfUrl = signedUrlData.signedUrl;
              setFileUrl(pdfUrl);
              
              // Apply watermarking to PDF
              try {
                console.log('Applying watermark to PDF version...');
                const watermarked = await watermarkPDF(pdfUrl, user.email, user.email);
                setWatermarkedUrl(watermarked);
                console.log('PDF watermark applied successfully');
              } catch (watermarkError) {
                console.error('PDF watermarking failed, using original:', watermarkError);
                setWatermarkedUrl(pdfUrl);
              }
              
              await logFileAccess();
              setLoading(false);
              return;
            }
          } catch (err) {
            console.warn('Error loading PDF version:', err);
            // Fall through to conversion attempt
          }
        }
        
        // If no PDF version exists, skip conversion attempt and show download message
        // Conversion should happen on upload, not on-demand viewing
        console.log('PowerPoint file detected - no PDF version found, showing download message');
        setError(null);
        setLoading(false);
        await logFileAccess();
        return;
      }

      // Clean the file path - remove any leading/trailing slashes or whitespace
      const cleanFilePath = item.file_path.trim();
      console.log('Cleaned file path:', cleanFilePath);

      // Verify file exists by checking if we can get its info
      try {
        const { data: fileInfo, error: infoError } = await supabase.storage
          .from('dataroom-files')
          .list(cleanFilePath.substring(0, cleanFilePath.lastIndexOf('/')), {
            limit: 100,
            offset: 0,
            sortBy: { column: 'name', order: 'asc' }
          });

        if (infoError) {
          console.warn('Could not list directory (non-fatal):', infoError);
        } else {
          const fileName = cleanFilePath.substring(cleanFilePath.lastIndexOf('/') + 1);
          const fileExists = fileInfo?.some(f => f.name === fileName);
          console.log('File exists in directory listing:', fileExists);
          if (!fileExists) {
            console.warn('File not found in directory listing. Files in directory:', fileInfo?.map(f => f.name));
          }
        }
      } catch (listErr) {
        console.warn('Error checking file existence (continuing anyway):', listErr);
      }

      // Get signed URL directly from Supabase storage
      // Try with cleaned path first
      let signedUrlData = null;
      let signedUrlError = null;
      
      try {
        const result = await supabase.storage
          .from('dataroom-files')
          .createSignedUrl(cleanFilePath, 3600); // 1 hour expiry
        
        signedUrlData = result.data;
        signedUrlError = result.error;
      } catch (err) {
        console.error('Exception creating signed URL:', err);
        signedUrlError = err;
      }

      if (signedUrlError) {
        console.error('Error creating signed URL:', signedUrlError);
        console.error('Error details:', {
          message: signedUrlError.message,
          statusCode: signedUrlError.statusCode,
          error: signedUrlError.error,
          name: signedUrlError.name,
          stack: signedUrlError.stack
        });
        
        // Try alternative: use download method if signed URL fails
        try {
          console.log('Attempting alternative download method...');
          const { data: downloadData, error: downloadError } = await supabase.storage
            .from('dataroom-files')
            .download(cleanFilePath);
          
          if (!downloadError && downloadData) {
            console.log('Download successful, creating blob URL');
            const blobUrl = URL.createObjectURL(downloadData);
            setFileUrl(blobUrl);
            
            // Apply watermark/convert based on file type
            if (item.file_type === 'application/pdf') {
              try {
                console.log('Applying watermark to PDF (alternative method)...');
                const watermarked = await watermarkPDF(blobUrl, user.email, user.email);
                setWatermarkedUrl(watermarked);
              } catch (watermarkError) {
                console.error('Watermarking failed, using original:', watermarkError);
                setWatermarkedUrl(blobUrl);
              }
            } else if (item.file_type && item.file_type.startsWith('image/')) {
              try {
                console.log('Applying watermark to image (alternative method)...');
                const watermarked = await watermarkImage(blobUrl, user.email);
                setWatermarkedUrl(watermarked);
              } catch (watermarkError) {
                console.error('Image watermarking failed, using original:', watermarkError);
                setWatermarkedUrl(blobUrl);
              }
            } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                       item.file_type === 'application/msword' ||
                       (item.name && (item.name.endsWith('.docx') || item.name.endsWith('.doc')))) {
              // Word document - convert to HTML with watermarking
              try {
                console.log('Converting Word document to HTML (alternative method)...');
                const html = await convertWordToHtml(blobUrl, user.email, item.name);
                setWordDocHtml(html);
              } catch (wordError) {
                console.error('Word document conversion failed:', wordError);
                setError(`Failed to preview Word document: ${wordError.message}`);
              }
            } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                       item.file_type === 'application/vnd.ms-excel' ||
                       (item.name && (item.name.endsWith('.xlsx') || item.name.endsWith('.xls')))) {
              // Excel file - convert to HTML with watermarking
              try {
                console.log('Converting Excel file to HTML (alternative method)...');
                const html = await convertExcelToHtml(blobUrl, user.email, item.name);
                setExcelHtml(html);
              } catch (excelError) {
                console.error('Excel conversion failed:', excelError);
                setError(`Failed to preview Excel file: ${excelError.message}`);
              }
            } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                       item.file_type === 'application/vnd.ms-powerpoint' ||
                       (item.name && (item.name.endsWith('.pptx') || item.name.endsWith('.ppt')))) {
              // PowerPoint file - skip conversion attempt (conversion should happen on upload)
              // Just set loading to false, the download message will be shown in render
              console.log('PowerPoint file detected (alternative path) - skipping conversion, will show download message');
              setError(null);
            } else if (item.file_type === 'text/plain' ||
                       (item.name && item.name.endsWith('.txt'))) {
              // Text file - convert to HTML with watermarking
              try {
                console.log('Converting text file to HTML (alternative method)...');
                const html = await convertTextToHtml(blobUrl, user.email, item.name);
                setTextContent(html);
              } catch (textError) {
                console.error('Text file conversion failed:', textError);
                setError(`Failed to preview text file: ${textError.message}`);
              }
            } else {
              setWatermarkedUrl(blobUrl);
            }
            
            await logFileAccess();
            setLoading(false);
            return;
          } else {
            console.error('Alternative download also failed:', downloadError);
          }
        } catch (altErr) {
          console.error('Alternative method error:', altErr);
        }
        
        // More helpful error message
        if (signedUrlError.message?.includes('Object not found') || signedUrlError.message?.includes('not found')) {
          throw new Error(
            `File not found in storage. The file may have been deleted or the upload may have failed.\n\n` +
            `Path: ${cleanFilePath}\n\n` +
            `Please try re-uploading the file.`
          );
        }
        
        if (signedUrlError.statusCode === 400) {
          throw new Error(
            `Invalid request when accessing file. This might be a storage configuration issue.\n\n` +
            `Path: ${cleanFilePath}\n\n` +
            `Error: ${signedUrlError.message || JSON.stringify(signedUrlError)}`
          );
        }
        
        throw new Error(`Failed to create signed URL: ${signedUrlError.message || JSON.stringify(signedUrlError)}`);
      }

      if (!signedUrlData || !signedUrlData.signedUrl) {
        throw new Error('No signed URL returned from storage');
      }

      console.log('Signed URL created successfully');
      const originalUrl = signedUrlData.signedUrl;
      setFileUrl(originalUrl);
      
      // Apply watermark/convert based on file type
      if (item.file_type === 'application/pdf') {
        try {
          console.log('Applying watermark to PDF...');
          const watermarked = await watermarkPDF(originalUrl, user.email, user.email);
          setWatermarkedUrl(watermarked);
          console.log('PDF watermark applied successfully');
        } catch (watermarkError) {
          console.error('PDF watermarking failed, using original:', watermarkError);
          setWatermarkedUrl(originalUrl);
        }
      } else if (item.file_type && item.file_type.startsWith('image/')) {
        try {
          console.log('Applying watermark to image...');
          const watermarked = await watermarkImage(originalUrl, user.email);
          setWatermarkedUrl(watermarked);
          console.log('Image watermark applied successfully');
        } catch (watermarkError) {
          console.error('Image watermarking failed, using original:', watermarkError);
          setWatermarkedUrl(originalUrl);
        }
      } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 item.file_type === 'application/msword' ||
                 (item.name && (item.name.endsWith('.docx') || item.name.endsWith('.doc')))) {
        // Word document - convert to HTML with watermarking
        try {
          console.log('Converting Word document to HTML...');
          const html = await convertWordToHtml(originalUrl, user.email, item.name);
          setWordDocHtml(html);
          console.log('Word document conversion completed successfully');
        } catch (wordError) {
          console.error('Word document conversion failed:', wordError);
          setError(`Failed to preview Word document: ${wordError.message}`);
        }
      } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                 item.file_type === 'application/vnd.ms-excel' ||
                 (item.name && (item.name.endsWith('.xlsx') || item.name.endsWith('.xls')))) {
        // Excel file - convert to HTML with watermarking
        try {
          console.log('Converting Excel file to HTML...');
          const html = await convertExcelToHtml(originalUrl, user.email, item.name);
          setExcelHtml(html);
          console.log('Excel conversion completed successfully');
        } catch (excelError) {
          console.error('Excel conversion failed:', excelError);
          setError(`Failed to preview Excel file: ${excelError.message}`);
        }
      } else if (item.file_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                 item.file_type === 'application/vnd.ms-powerpoint' ||
                 (item.name && (item.name.endsWith('.pptx') || item.name.endsWith('.ppt')))) {
        // PowerPoint file - skip conversion attempt (conversion should happen on upload)
        // Just set loading to false, the download message will be shown in render
        console.log('PowerPoint file detected (fallback path) - skipping conversion, will show download message');
        setError(null);
      } else if (item.file_type === 'text/plain' ||
                 (item.name && item.name.endsWith('.txt'))) {
        // Text file - convert to HTML with watermarking
        try {
          console.log('Converting text file to HTML...');
          const html = await convertTextToHtml(originalUrl, user.email, item.name);
          setTextContent(html);
          console.log('Text file conversion completed successfully');
        } catch (textError) {
          console.error('Text file conversion failed:', textError);
          setError(`Failed to preview text file: ${textError.message}`);
        }
      } else {
        // For other file types, use original URL
        setWatermarkedUrl(originalUrl);
      }
      
      // Log file access
      await logFileAccess(); 

      setLoading(false);
    } catch (error) {
      console.error('Error loading file:', error);
      setError(error.message);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, projectId, user, logFileAccess]); // convertExcelToHtml, convertWordToHtml, etc. are stable useCallback hooks
 
  useEffect(() => {
    if (isOpen && item && user) {
      loadFile();
    }
  }, [isOpen, item, user, loadFile]);
  
  // Cleanup blob URLs on unmount or when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Revoke all tracked blob URLs
      blobUrlsRef.current.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('Error revoking blob URL:', e);
        }
      });
      blobUrlsRef.current = [];
      setFileUrl(null);
      setWatermarkedUrl(null);
      setWordDocHtml(null);
      setExcelHtml(null);
      setPowerpointPdfUrl(null);
      setTextContent(null);
    }
    
    return () => {
      // Cleanup on unmount
      blobUrlsRef.current.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('Error revoking blob URL:', e);
        }
      });
    };
  }, [isOpen]);
 
  const handleDownload = async () => {
    try {
      // Always download the ORIGINAL file (not converted PDF for PowerPoint)
      // For PowerPoint, we want the original PPTX, not the converted PDF
      const isPowerPoint = item.file_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                          item.file_type === 'application/vnd.ms-powerpoint' ||
                          (item.name && (item.name.endsWith('.pptx') || item.name.endsWith('.ppt')));
      
      let urlToDownload;
      
      if (isPowerPoint) {
        // For PowerPoint, create signed URL on-demand for download (since we skip it for viewing)
        if (!item.file_path) {
          console.error('Cannot download: file path not found');
          return;
        }
        
        try {
          const cleanFilePath = item.file_path.trim();
          const { data: signedUrlData, error: signedUrlError } = await supabase.storage
            .from('dataroom-files')
            .createSignedUrl(cleanFilePath, 3600);
          
          if (signedUrlError || !signedUrlData?.signedUrl) {
            throw new Error(signedUrlError?.message || 'Failed to create download URL');
          }
          
          urlToDownload = signedUrlData.signedUrl;
        } catch (error) {
          console.error('Error creating download URL for PowerPoint:', error);
          alert(`Failed to prepare file for download: ${error.message}`);
          return;
        }
      } else {
        // For other files, use watermarked version if available, otherwise original
        urlToDownload = watermarkedUrl || fileUrl;
        if (!urlToDownload) return;
      }

      // Download file
      const link = document.createElement('a');
      link.href = urlToDownload;
      link.download = item.name; // Original filename (PPTX, not PDF)
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Download error:', error);
      alert(`Download failed: ${error.message}`);
    }
  };

  const renderFilePreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }
 
    if (error) {
      const handleDeleteOrphanedRecord = async () => {
        if (!window.confirm(`Delete this file record?\n\nThis will remove the database entry for "${item.name}" since the actual file doesn't exist in storage.\n\nThis action cannot be undone.`)) {
          return;
        }

        try {
          // First check if user has permission to delete
          // Get the user's role for this project
          const { data: userRole, error: roleError } = await supabase
            .from('dataroom_users')
            .select('role')
            .eq('user_id', user.id)
            .eq('project_id', projectId)
            .single();

          console.log('User role:', userRole);

          if (roleError) {
            console.error('Error checking user role:', roleError);
          }

          // Check if user is creator or created this file
          const isCreator = userRole?.role === 'creator';
          const isFileOwner = item.created_by === user.id;

          if (!isCreator && !isFileOwner) {
            alert('You do not have permission to delete this file record.\n\nOnly the project creator or file owner can delete records.\n\nPlease contact your administrator.');
            return;
          }

          // Delete related records first (cascade delete)
          console.log('Deleting related permissions for item:', item.id);
          
          // Delete from dataroom_file_permissions (Teams-based permissions)
          const { error: permError } = await supabase
            .from('dataroom_file_permissions')
            .delete()
            .eq('dataroom_item_id', item.id);

          if (permError) {
            console.warn('Error deleting permissions (may not exist):', permError);
            // Don't fail here - permissions might not exist
          }

          // Now delete the item itself
          const { error: deleteError } = await supabase
            .from('dataroom_items')
            .delete()
            .eq('id', item.id);

          if (deleteError) {
            console.error('Delete error:', deleteError);
            
            // Provide specific error messages
            if (deleteError.code === '42501') {
              throw new Error('Permission denied. You may need creator/admin privileges to delete this record.');
            } else if (deleteError.code === '23503') {
              throw new Error(`Cannot delete: This file still has related records in the database.\n\nError: ${deleteError.details}\n\nPlease contact your administrator.`);
            } else if (deleteError.message.includes('policy')) {
              throw new Error('Database security policy prevents deletion. Please contact your administrator.');
            } else {
              throw new Error(deleteError.message);
            }
          }

          alert('Orphaned record deleted successfully. Refreshing...');
          onClose(); // Close the modal first
          
          // Reload the parent component instead of full page
          if (window.location.pathname.includes('/dealroom/') || window.location.pathname.includes('/dataroom/')) {
            setTimeout(() => window.location.reload(), 100);
          }
        } catch (err) {
          console.error('Error deleting record:', err);
          alert(`Failed to delete record:\n\n${err.message}\n\nPlease contact an administrator if you believe you should have access.`);
        }
      };

      return (
        <div className="flex flex-col items-center justify-center py-8 px-8 max-h-[600px] overflow-y-auto">
          <AlertCircle className="h-12 w-12 mb-3 text-red-500" />
          <p className="text-base font-medium mb-2 text-gray-900">File Not Found in Storage</p>
          
          {/* Compact error message */}
          <p className="text-xs text-gray-600 mb-3 max-w-lg text-center">
            The file record exists but the actual file is missing from storage (likely a failed upload).
          </p>
          
          {/* Compact file info */}
          <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 p-2 rounded mb-3 max-w-lg w-full">
            <p className="font-semibold mb-1 text-yellow-800 text-xs">📋 File Details:</p>
            <div className="space-y-0.5">
              <p><strong>Name:</strong> {item.name}</p>
              <p><strong>Type:</strong> {item.file_type || 'Unknown'}</p>
              <p><strong>Path:</strong> <span className="text-xs font-mono">{item.file_path || 'Not set'}</span></p>
            </div>
          </div>

          {/* Compact instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3 max-w-lg">
            <p className="text-xs text-blue-700">
              <strong>To fix:</strong> Click "Delete Record" to clean up (requires creator/admin access).
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleDeleteOrphanedRecord}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Record
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      );
    }
 
    const { file_type } = item;
    
    // Check if it's a PowerPoint file (used throughout render)
    const isPowerPointFile = file_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                             file_type === 'application/vnd.ms-powerpoint' ||
                             (item.name && (item.name.endsWith('.pptx') || item.name.endsWith('.ppt')));

    if (file_type && file_type.startsWith('image/')) {
      return (
        <div className="flex justify-center p-4">
          <img
            src={watermarkedUrl || fileUrl}
            alt={item.name}
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    }
 
    if (file_type === 'application/pdf') {
      return (
        <div className="h-[70vh] w-full">
          {/* Note: PDF viewer libraries usually handle the PDF's content, 
              but the document itself should have the watermark embedded by the Python download endpoint if requested. 
              The PDF will be delivered via the signed URL.
          */}
          <iframe
            src={`${watermarkedUrl || fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full h-full border-0 rounded-lg"
            title={item.name}
            onError={() => setError('Failed to load PDF')}
          />
        </div>
      );
    }
 
    // Word documents - display as HTML
    if (wordDocHtml) {
      return (
        <div className="h-[70vh] w-full overflow-auto border rounded-lg">
          <iframe
            srcDoc={wordDocHtml}
            className="w-full h-full border-0"
            title={item.name}
            sandbox="allow-same-origin"
          />
        </div>
      );
    }

    // Excel files - display as HTML
    if (excelHtml) {
      return (
        <div className="h-[70vh] w-full overflow-auto border rounded-lg">
          <iframe
            srcDoc={excelHtml}
            className="w-full h-full border-0"
            title={item.name}
            sandbox="allow-same-origin"
          />
        </div>
      );
    }

    // PowerPoint files - show PDF version if available, otherwise show download message
    // isPowerPointFile is already declared above
    
    // If PowerPoint has PDF version, show it (watermarked PDF will be displayed above)
    // If no PDF version and no error, show download message
    if (isPowerPointFile && !loading && !error && !watermarkedUrl && !fileUrl && !powerpointPdfUrl) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-8 max-h-[600px]">
          <FileText className="h-16 w-16 mb-4 text-blue-500" />
          <h3 className="text-xl font-semibold mb-2 text-gray-900">PowerPoint File</h3>
          <p className="text-gray-600 mb-6 text-center max-w-md">
            {item.pdf_file_path 
              ? 'PDF version is being prepared. Please try again in a moment or download the original file.'
              : 'PowerPoint files cannot be previewed in the browser. Please download the file to view it.'}
          </p>
          <button
            onClick={handleDownload}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Download className="h-5 w-5" />
            Download PowerPoint File
          </button>
        </div>
      );
    }

    // Text files - display as HTML
    if (textContent) {
      return (
        <div className="h-[70vh] w-full overflow-auto border rounded-lg">
          <iframe
            srcDoc={textContent}
            className="w-full h-full border-0"
            title={item.name}
            sandbox="allow-same-origin"
          />
        </div>
      );
    }

    // Check if it's a Word document (by MIME type or extension)
    const isWordDoc = file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                      file_type === 'application/msword' ||
                      (item.name && (item.name.endsWith('.docx') || item.name.endsWith('.doc')));

    // Check if it's an Excel file
    const isExcelFile = file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                        file_type === 'application/vnd.ms-excel' ||
                        (item.name && (item.name.endsWith('.xlsx') || item.name.endsWith('.xls')));

    // Check if it's a text file
    const isTextFile = file_type === 'text/plain' ||
                       (item.name && item.name.endsWith('.txt'));

    if ((isWordDoc && !wordDocHtml && !loading) || 
        (isExcelFile && !excelHtml && !loading) ||
        (isTextFile && !textContent && !loading)) {
      // File is being processed
      return (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (file_type && ((file_type.startsWith('text/') && !isTextFile) || 
                      (file_type.includes('document') && !isWordDoc && !isExcelFile && !isPowerPointFile) || 
                      (file_type.includes('sheet') && !isExcelFile))) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          <FileText className="h-16 w-16 mb-4" />
          <p className="text-lg font-medium mb-2">{item.name}</p>
          <p className="text-sm mb-4">File preview not available for this type</p>
          <button
            onClick={handleDownload}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Download to View
          </button>
        </div>
      );
    }
 
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        <FileText className="h-16 w-16 mb-4" />
        <p className="text-lg font-medium mb-2">Preview not available</p>
        <p className="text-sm">File type: {file_type || 'Unknown'}</p>
        <button
          onClick={handleDownload}
          className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Download className="h-4 w-4 mr-2" />
          Download File
        </button>
      </div>
    );
  };
 
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
 
  if (!isOpen) return null;
 
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[95vh] mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center">
            <div className="flex items-center mr-4">
              {item.file_type && item.file_type.startsWith('image/') ? (
                <ImageIcon className="h-6 w-6 text-blue-600 mr-2" />
              ) : (
                <FileText className="h-6 w-6 text-blue-600 mr-2" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                <p className="text-sm text-gray-500">
                  {item.file_size && formatFileSize(item.file_size)} • {item.file_type || 'Unknown type'}
                </p>
              </div>
            </div>
          </div>
 
          <div className="flex items-center space-x-3">
            {/* Watermark indicator */}
            <div className="flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs">
              <Shield className="h-3 w-3 mr-1" />
              Watermarked
            </div>

            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={!fileUrl && !item.file_path}
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </button>
 
            {/* Close button */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
 
        {/* File Preview Area */}
        <div className="flex-1 overflow-auto bg-gray-100">
          {renderFilePreview()}
        </div>
 
        {/* Footer with user info */}
        <div className="p-3 border-t bg-gray-50 text-center">
          <p className="text-xs text-gray-500">
            Viewed by {user.email} on {formatDateTime(new Date())} • 
            This document is confidential and watermarked for security
          </p>
        </div>
      </div>
    </div>
  );
};
 
export default DocumentViewer;